import { Route, Routes, useLocation, Outlet, useParams } from "react-router-dom";
import { useEffect } from "react";

import LoginPage from "@rapid/pages/LoginPage";
import NotFound from "@rapid/pages/NotFound";
import { SetupRoutes } from "./SetupRoutes";
import { useGuardBuildSteps } from "@rapid/hooks/guards/useGuardBuildSteps";
import { GuardedRoute } from "@rapid/components/routing/GuardedRoute";
import { useGuardWorkflow } from "@rapid/hooks/guards/useGuardWorkflow";
import { useGuardWorkspace } from "@rapid/hooks/guards/useGuardWorkspace";
import { useGuardNoWorkspace } from "@rapid/hooks/guards/useGuardNoWorkspace";
import { HydrateWorkflow } from "@rapid/hydrate/HydrateWorkflow";
import { HydratePublicWorkflow } from "@rapid/hydrate/HydratePublicWorkflow";
import { useGuardUpload } from "@rapid/hooks/guards/useGuardUpload";
import { UploadVisualAreaContent } from "@rapid/components/flow/build/Steps/Upload/VisualAreaContent";
import { BuildPage } from "@rapid/pages/BuildPage";
import { SharePage } from "@rapid/pages/SharePage";
import { WorkflowPage } from "@rapid/pages/WorkflowPage";
import { ModelsPage } from "@rapid/pages/ModelsPage";
import { AnimatePresence } from "framer-motion";
import { Navbar } from "@rapid/components/layout/Navbar";
import { TutorialCard } from "@rapid/components/tutorial/TutorialCard";
import { RapidDataIdRedirect } from "@rapid/pages/RapidDataIdRedirect";
import { WorkpaceIdWorkflowIdRedirect } from "@rapid/pages/WorkpaceIdWorkflowIdRedirect";
import { useAnalytics } from "@rapid/contexts/AnalyticsContext";
import { AnalyticsPageView } from "@rapid/analytics/types";
import { StepToAnalyticsPageViewMap } from "@rapid/analytics/types";
import { useFlowState } from "@rapid/hooks/useFlowState";
import { useAuth } from "@rapid/contexts/AuthContext";
import { useCurrentWorkspace } from "@rapid/hooks/useCurrentWorkspace";
import { useWorkspaces } from "@rapid/queries/workspaceQueries";
import useApi from "@rapid/hooks/useApi";
import { RapidDataInitializer } from "@rapid/components/RapidDataInitializer";
function AppLayout() {
    const { flow, step } = useFlowState();
    const location = useLocation();
    const { currentPage, setCurrentPage } = useAnalytics();

    useEffect(() => {
        if (currentPage.path !== location.pathname) {
            const pageName = !(flow && step)
                ? AnalyticsPageView.HOME
                : StepToAnalyticsPageViewMap[step];
            setCurrentPage({ name: pageName, path: location.pathname });
        }
    }, [flow, location.pathname, step, currentPage, setCurrentPage]);

    const { user, activeSession, apiAuthenticated } = useAuth();
    const api = useApi();
    const {
        data: workspacesData,
        isLoading: workspaceLoading,
        isFetched: workspacesHasAPIFetched
    } = useWorkspaces();
    const workspaces = workspacesData?.workspaces;
    const { currentWorkspace, switchWorkspace } = useCurrentWorkspace();
    const { workspaceUrl } = useParams<{ workspaceUrl: string }>();

    // when the workspaceURl changes, switch to that workspace
    useEffect(() => {
        if (workspaceUrl && currentWorkspace && workspaceUrl !== currentWorkspace.url) {
            const newWorkspace = workspaces?.find((w) => {
                return w.url === workspaceUrl;
            });
            if (newWorkspace) {
                switchWorkspace(newWorkspace.id);
            }
        }
    }, [workspaceUrl, currentWorkspace, workspaces, switchWorkspace]);

    const isSharePage = location.pathname.includes("/p/");
    const mainClassName = isSharePage
        ? "flex flex-shrink-0 flex-col border-t"
        : "flex min-h-0 flex-1 flex-col overflow-hidden border-t";

    return (
        <AnimatePresence mode="wait" initial={false}>
            <div className="flex h-dvh flex-col overflow-hidden bg-white md:overflow-y-auto">
                <Navbar />
                <TutorialCard />
                <RapidDataInitializer />
                <main className={mainClassName}>
                    <Outlet />
                </main>
            </div>
        </AnimatePresence>
    );
}

export function AppRoutes() {
    const location = useLocation();

    // Note: key={location.pathname} forces React to remount the entire Routes tree on navigation
    // This prevents hook order violations when switching between routes with different guards
    return (
        <Routes location={location} key={location.pathname}>
            <Route element={<AppLayout />}>
                <Route path="/redirect">
                    <Route path="id/:rapidDataId" element={<RapidDataIdRedirect />} />
                    <Route
                        path="workspace-id/:workspaceId/workflow-id/:workflowId"
                        element={<WorkpaceIdWorkflowIdRedirect />}
                    />
                </Route>
                <Route
                    element={
                        <GuardedRoute useGuardStep={useGuardNoWorkspace}>
                            <Outlet />
                        </GuardedRoute>
                    }
                >
                    <Route path="/login" element={<LoginPage />} />
                    {/* <Route path="/logout" element={<LogoutPage />} /> */}

                    <Route
                        path="/"
                        element={
                            <GuardedRoute useGuardStep={useGuardUpload}>
                                <UploadVisualAreaContent />
                            </GuardedRoute>
                        }
                    />
                    {/* Share routes with /p/ prefix - redirects to workspace-scoped route if logged in */}
                    <Route
                        path="p/:shareableSlug"
                        element={
                            <HydratePublicWorkflow>
                                <SharePage />
                            </HydratePublicWorkflow>
                        }
                    />
                    <Route
                        path="build/:step"
                        element={
                            <GuardedRoute useGuardStep={useGuardBuildSteps}>
                                <BuildPage />
                            </GuardedRoute>
                        }
                    />
                </Route>
                <Route
                    path=":workspaceUrl/*"
                    element={
                        <GuardedRoute useGuardStep={useGuardWorkspace}>
                            <Outlet />
                        </GuardedRoute>
                    }
                >
                    <Route path="models" element={<ModelsPage />} />
                    <Route
                        path="p/:shareableSlug"
                        element={
                            <HydratePublicWorkflow>
                                <SharePage />
                            </HydratePublicWorkflow>
                        }
                    />
                    <Route
                        path="build/:step"
                        element={
                            <GuardedRoute useGuardStep={useGuardBuildSteps}>
                                <BuildPage />
                            </GuardedRoute>
                        }
                    />
                    <Route
                        path=":workflowUrl/:step/:sourceId?"
                        element={
                            <HydrateWorkflow>
                                <GuardedRoute useGuardStep={useGuardWorkflow}>
                                    <WorkflowPage />
                                </GuardedRoute>
                            </HydrateWorkflow>
                        }
                    />
                    <Route path="*" element={<SetupRoutes />} />
                </Route>
                <Route path="*" element={<NotFound />} />
            </Route>
        </Routes>
    );
}
